import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { FieldEntry } from '@/lib/export';

interface SelectedColumnsProps {
  selected: FieldEntry[];
  labelFor: (fieldId: string) => string;
  onReorder: (next: FieldEntry[]) => void;
  onRename: (fieldId: string, header: string | undefined) => void;
  onRemove: (fieldId: string) => void;
}

/** The ordered list of chosen columns — drag to reorder, edit header, remove. */
export function SelectedColumns({
  selected,
  labelFor,
  onReorder,
  onRename,
  onRemove,
}: SelectedColumnsProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = selected.findIndex((s) => s.fieldId === active.id);
    const newIdx = selected.findIndex((s) => s.fieldId === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder(arrayMove(selected, oldIdx, newIdx));
  };

  if (selected.length === 0) {
    return (
      <div className="flex h-full min-h-[12rem] flex-col items-center justify-center rounded-lg border border-dashed border-border text-center">
        <p className="text-sm text-muted-foreground">No columns yet</p>
        <p className="mt-1 text-xs text-muted-foreground">Pick fields from the catalog to build your export.</p>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={selected.map((s) => s.fieldId)} strategy={verticalListSortingStrategy}>
        <ol className="space-y-1.5">
          {selected.map((entry, i) => (
            <SortableColumnRow
              key={entry.fieldId}
              entry={entry}
              index={i}
              defaultLabel={labelFor(entry.fieldId)}
              onRename={onRename}
              onRemove={onRemove}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

interface RowProps {
  entry: FieldEntry;
  index: number;
  defaultLabel: string;
  onRename: (fieldId: string, header: string | undefined) => void;
  onRemove: (fieldId: string) => void;
}

function SortableColumnRow({ entry, index, defaultLabel, onRename, onRemove }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.fieldId,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="group flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5"
    >
      <div
        {...attributes}
        {...listeners}
        className="flex w-4 shrink-0 cursor-grab items-center justify-center text-muted-foreground opacity-40 transition-opacity touch-none group-hover:opacity-100 active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <span className="w-5 shrink-0 text-center text-xs tabular-nums text-muted-foreground">{index + 1}</span>
      <Input
        value={entry.header ?? ''}
        onChange={(e) => onRename(entry.fieldId, e.target.value || undefined)}
        placeholder={defaultLabel}
        className={cn('h-8 flex-1 border-transparent bg-transparent px-2 hover:border-border focus:border-border')}
      />
      <button
        type="button"
        onClick={() => onRemove(entry.fieldId)}
        className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Remove ${defaultLabel}`}
      >
        <X className="h-4 w-4" />
      </button>
    </li>
  );
}
